/* eslint-disable react-refresh/only-export-components -- helpers + EditField co-locate for cohesion */
import type { StageEditField } from './stageData';

export function formatValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v)) {
    if (v.length === 0) return '—';
    const head = v.slice(0, 3).map((x) => (typeof x === 'string' ? x : JSON.stringify(x)));
    return `[${v.length}] ${head.join(', ')}${v.length > 3 ? ' …' : ''}`;
  }
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'number' || typeof v === 'bigint' || typeof v === 'string') return String(v);
  return JSON.stringify(v);
}

export function fieldFrom(json: Record<string, unknown> | null, key: string): unknown {
  if (!json) return undefined;
  return json[key];
}

export function stringifyEditValue(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return JSON.stringify(v);
}

export function coerce(value: string, kind: StageEditField['kind']): unknown {
  if (kind === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n : value;
  }
  if (kind === 'select' && (value === 'true' || value === 'false')) {
    return value === 'true';
  }
  if (kind === 'textarea' && value.includes(',')) {
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return value;
}

export function EditField({
  field,
  value,
  onChange,
}: {
  field: StageEditField;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium" htmlFor={`edit-${field.key}`}>
        {field.label}
      </label>
      {field.kind === 'textarea' ? (
        <textarea
          id={`edit-${field.key}`}
          className="w-full rounded border bg-background p-2 text-sm"
          rows={3}
          value={value}
          onChange={(e) => { onChange(e.target.value); }}
        />
      ) : field.kind === 'select' ? (
        <select
          id={`edit-${field.key}`}
          className="w-full rounded border bg-background p-2 text-sm"
          value={value}
          onChange={(e) => { onChange(e.target.value); }}
        >
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : (
        <input
          id={`edit-${field.key}`}
          type={field.kind === 'number' ? 'number' : 'text'}
          className="w-full rounded border bg-background p-2 text-sm"
          value={value}
          onChange={(e) => { onChange(e.target.value); }}
        />
      )}
      {field.hint && (
        <p className="text-xs text-muted-foreground">{field.hint}</p>
      )}
    </div>
  );
}
